import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { SolIconComponent } from '../sol-icon/sol-icon.component';
import { SolSpinnerComponent } from '../sol-spinner/sol-spinner.component';
import { SolIcon } from '@sol/icons';
import { AbstractComponent } from '../../abstract.component';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import {
  SolButton,
  SolButtonDropdownItem,
  SolButtonMode,
  SolButtonSize,
  SolButtonType,
} from '../../../models/core/sol-button';
import { PopoverService } from '../../../services/popover.service';

@Component({
  selector: 'sol-button',
  imports: [SolIconComponent, SolSpinnerComponent, TranslatePipe],
  templateUrl: './sol-button.component.html',
})
export class SolButtonComponent extends AbstractComponent<SolButton> {
  // Static references
  protected readonly SolIcon = SolIcon;

  // Dependencies
  private readonly popoverService = inject(PopoverService, { optional: true });

  // Computed
  protected readonly disabled$ = this.compute(state => state.disabled);
  protected readonly loading$ = this.compute(state => state.loading);
  protected readonly icon$ = this.compute(state => state.icon);
  protected readonly text$ = this.compute(state => state.text);
  protected readonly appendIcon$ = this.compute(state => state.appendIcon);
  protected readonly class$ = this.compute(state => 'sol-button-' + state.type);
  protected readonly isIcon$ = this.compute(state => state.mode === SolButtonMode.icon);
  protected readonly isCustom$ = this.compute(state => state.mode === SolButtonMode.custom);
  protected readonly isSmall$ = this.compute(state => state.size === SolButtonSize.small);
  protected readonly ariaLabel$ = this.compute(state => state.ariaLabel);
  protected readonly hasDropdown$ = this.compute(state => state.dropdownItems.length > 0);

  // Events
  @Output('click')
  private readonly clickEmitter = new EventEmitter<MouseEvent>();
  @Output('auxclick')
  private readonly auxclickEmitter = new EventEmitter<MouseEvent>();
  @Output('dropdownSelect')
  readonly dropdownSelect = new EventEmitter<string>();

  constructor() {
    super(SolButton.of());
  }

  @Input()
  public set button(button: SolButton) {
    this.patch(button);
  }

  @Input()
  public set type(type: SolButtonType) {
    this.update(state => state.withType(type));
  }

  @Input()
  public set disabled(disabled: boolean) {
    this.update(state => state.withDisabled(disabled));
  }

  @Input()
  public set loading(loading: boolean) {
    this.update(state => state.withLoading(loading));
  }

  @Input()
  public set icon(icon: SolIcon | undefined) {
    this.update(state => state.withIcon(icon));
  }

  @Input()
  public set text(text: string | undefined) {
    this.update(state => state.withText(text));
  }

  @Input()
  public set appendIcon(appendIcon: SolIcon | undefined) {
    this.update(state => state.withAppendIcon(appendIcon));
  }

  @Input()
  public set small(small: boolean) {
    this.update(state => state.withSmall(small));
  }

  @Input()
  public set custom(custom: boolean) {
    this.update(state => state.withCustom(custom));
  }

  @Input()
  public set ariaLabel(ariaLabel: string | undefined) {
    this.update(state => state.withAriaLabel(ariaLabel));
  }

  @Input()
  public set dropdownItems(items: readonly SolButtonDropdownItem[]) {
    this.update(state => state.withDropdownItems(items));
  }

  click(event: any) {
    if (this.state.disabled || this.state.loading) {
      return;
    }
    event.stopPropagation();
    this.clickEmitter.emit(event);
  }

  auxclick(event: any) {
    if (this.state.disabled || this.state.loading) {
      return;
    }
    event.stopPropagation();
    this.auxclickEmitter.emit(event);
  }

  dropdownChevronClick(event: MouseEvent): void {
    if (this.state.disabled || this.state.loading) return;
    event.stopPropagation();
    const items = this.state.dropdownItems;
    if (!items.length || !this.popoverService) return;
    const menuGroups = [
      {
        items: items.map(item => ({
          id: item.id,
          name: item.label,
          icon: item.icon,
        })),
      },
    ];
    this.popoverService.showMenu(
      menuGroups,
      (actionId: string) => {
        this.dropdownSelect.emit(actionId);
        return true;
      },
      { boundsEvent: event },
    );
  }
}
